<script setup>
import { computed, onMounted, reactive, ref } from 'vue'
import { query } from '../../db'
import { fromDb } from '../../stores/live'
import { stepBy } from '../../lib/recurrence'
import Sheet from '../ui/Sheet.vue'
import Icon from '../ui/Icon.vue'
import Kbd from '../ui/Kbd.vue'
import { ariaCombo } from '../../lib/shortcuts'
import Picker from '../ui/Picker.vue'
import { fmt, withCategory } from '../../stores'
import { state, closeSheet, toast } from '../../stores/state'
import { count } from '../../stores/usage'
import { t } from '../../stores/i18n'
import { addPlan, deletePlan, updatePlan } from '../../stores/plans'
import { CADENCES, cadenceKey, monthlyEquivalent, nextOccurrence } from '../../lib/recurrence'
import { AUTO_PERIODS_DEFAULT, withAutoAmounts } from '../../lib/matching'
import { currencySymbol, parseAmount } from '../../lib/money'
import { today as todayISO } from '../../lib/dates'

const props = defineProps({ payload: { type: Object, default: () => ({}) } })

const existing = computed(() =>
  props.payload?.id ? state.plans.find((p) => p.id === props.payload.id) : null,
)

// Only the first has a word in it; the rest are the same in every language.
const TOLERANCES = [
  { value: 0, label: () => t('tolerance.exact') },
  { value: 0.02, label: () => '±2%' },
  { value: 0.05, label: () => '±5%' },
  { value: 0.1, label: () => '±10%' },
  { value: 0.25, label: () => '±25%' },
  { value: 0.5, label: () => '±50%' },
]

const form = reactive({
  type: props.payload?.type ?? 'expense',
  amount: '',
  cadence: 'monthly',
  start_date: todayISO(),
  end_date: '',
  category: '',
  tolerance: 0.1,
  archived: false,
  auto: false,
  auto_periods: String(AUTO_PERIODS_DEFAULT),
})
/** A plan has one category, so picking the one it has takes it off. */
const categoryLook = (name) => (form.category === name ? { tone: 'on' } : { tone: 'off' })
const setCategory = (name) => (form.category = form.category === name ? '' : name)

const error = ref('')
const confirmDelete = ref(false)

onMounted(() => {
  const p = existing.value
  if (p)
    Object.assign(form, {
      ...p,
      amount: p.amount ? String(p.amount) : '',
      end_date: p.end_date ?? '',
      archived: !!p.archived,
      auto: !!p.auto,
      auto_periods: String(p.auto_periods ?? AUTO_PERIODS_DEFAULT),
    })
})

/** A one-off has no earlier occurrences, so it cannot be automatic. */
const canAuto = computed(() => form.cadence !== 'once')
const automatic = computed(() => form.auto && canAuto.value)

const autoPeriods = computed(() => {
  const n = Number(String(form.auto_periods).trim())
  return Number.isInteger(n) && n >= 1 && n <= 120 ? n : null
})

/**
 * The draft's category over the occurrences it would average, asked of the
 * database as the form changes — from a step further back than it looks, so
 * the nearest-slot split at the edge sees its neighbours.
 */
const previewRows = fromDb(
  () => {
    if (!automatic.value || !autoPeriods.value || !form.category.trim()) return null
    const today = todayISO()
    return {
      category: form.category.trim(),
      from: stepBy(form.cadence, today, -(autoPeriods.value + 2)),
      to: stepBy(form.cadence, today, 1),
    }
  },
  ({ category, from, to }) =>
    query(
      `SELECT t.id, t.date, t.amount, c.name AS category
         FROM transactions t JOIN categories c ON c.id = t.category_id
        WHERE lower(c.name) = lower(?) AND t.date BETWEEN ? AND ?`,
      [category, from, to],
    ),
  [],
)

/**
 * What the plan would expect next, worked out exactly as the panel will — with
 * the other plans alongside, since plans sharing a category split its history.
 */
const autoPreview = computed(() => {
  if (!automatic.value || !autoPeriods.value || !form.category.trim()) return null
  const draft = {
    ...form,
    id: existing.value?.id ?? 'draft',
    category: form.category.trim(),
    amount: 0,
    archived: 0,
    auto: 1,
    auto_periods: autoPeriods.value,
    end_date: form.end_date || null,
  }
  const plans = [...state.plans.filter((p) => p.id !== draft.id), draft]
  const resolved = withAutoAmounts(plans, previewRows.value, todayISO()).find((p) => p.id === draft.id)
  return resolved ? { amount: resolved.amount, basis: resolved.autoBasis } : null
})

const symbol = computed(() => currencySymbol(state.settings.currency))
const preview = computed(() => {
  const amount = automatic.value ? (autoPreview.value?.basis ? autoPreview.value.amount : NaN) : parseAmount(form.amount)
  if (!Number.isFinite(amount)) return null
  const plan = { ...form, amount: Math.abs(amount) }
  return {
    monthly: monthlyEquivalent(plan),
    next: nextOccurrence(plan, todayISO()),
  }
})

async function save() {
  const amount = parseAmount(form.amount)
  if (!form.category.trim()) return (error.value = t('planSheet.needCategory'))
  if (automatic.value) {
    if (!autoPeriods.value) return (error.value = t('planSheet.needPeriods'))
  } else if (!Number.isFinite(amount) || amount === 0) {
    return (error.value = t('planSheet.needAmount'))
  }
  const payload = {
    ...form,
    amount: Number.isFinite(amount) ? amount : 0,
    auto: automatic.value,
    auto_periods: autoPeriods.value ?? AUTO_PERIODS_DEFAULT,
    end_date: form.end_date || null,
  }
  if (existing.value) await updatePlan(existing.value.id, payload)
  else {
    count('plan.add')
    await addPlan(payload)
  }
  toast(t(existing.value ? 'planSheet.updated' : 'planSheet.added'), 'good')
  closeSheet()
}

async function destroy() {
  if (!confirmDelete.value) return (confirmDelete.value = true)
  await deletePlan(existing.value.id)
  toast(t('planSheet.deleted'))
  closeSheet()
}
</script>

<template>
  <Sheet
    :title="t(existing ? 'planSheet.edit' : 'plan.add')"
    :subtitle="t('planSheet.subtitle')"
    @close="closeSheet"
  >
    <div class="grid gap-4">
      <div class="grid grid-cols-2 gap-1 rounded-xl bg-raised p-1">
        <button
          v-for="t in [
            { value: 'expense', label: t('planSheet.plannedExpense') },
            { value: 'income', label: t('plan.income') },
          ]"
          :key="t.value"
          class="rounded-lg py-2 text-[13.5px] font-semibold transition"
          :class="form.type === t.value ? 'bg-surface text-text shadow-sm' : 'text-muted'"
          @click="form.type = t.value"
        >
          {{ t.label }}
        </button>
      </div>

      <div class="grid gap-3" :class="!automatic && 'grid-cols-[1fr_auto]'">
        <div>
          <!-- The category is what the plan is: "every 2nd, something in
               Electricity". It leads, and it is not optional. -->
          <label class="label" for="pl-cat">{{ t('common.category') }}</label>
          <Picker
            id="pl-cat"
            single
            :create-label="t('common.use')"
            :placeholder="t('planSheet.categoryPlaceholder')"
            :value="form.category"
            :options="withCategory(form.category)"
            :look="categoryLook"
            @pick="setCategory"
            @create="setCategory"
          />
        </div>
        <div v-if="!automatic" class="w-36">
          <label class="label" for="pl-amount">{{ t('common.amount') }}</label>
          <div class="relative">
            <span class="absolute top-1/2 left-3 -translate-y-1/2 text-faint">{{ symbol }}</span>
            <input id="pl-amount" v-model="form.amount" class="input tnum pl-7" inputmode="decimal" placeholder="0.00" />
          </div>
        </div>
      </div>

      <label v-if="canAuto" class="flex items-center gap-2.5 text-[13.5px]">
        <input v-model="form.auto" type="checkbox" class="size-4 accent-[var(--c-accent)]" />
        {{ t('planSheet.auto') }}
      </label>

      <div v-if="automatic">
        <div class="grid grid-cols-2 gap-3">
          <div>
            <label class="label" for="pl-auto-periods">{{ t('planSheet.autoPeriods') }}</label>
            <input
              id="pl-auto-periods"
              v-model="form.auto_periods"
              type="number"
              inputmode="numeric"
              min="1"
              max="120"
              step="1"
              class="input tnum"
              :class="!autoPeriods && '!border-danger'"
              :aria-invalid="!autoPeriods"
            />
          </div>
          <div>
            <label class="label" for="pl-auto-average">{{ t('planSheet.autoAverage') }}</label>
            <output
              id="pl-auto-average"
              for="pl-auto-periods"
              class="input tnum block truncate bg-raised/50 font-semibold"
              :class="!autoPreview?.basis && 'font-normal text-faint'"
              aria-live="polite"
            >
              {{ autoPreview?.basis ? fmt(autoPreview.amount) : '—' }}
            </output>
          </div>
        </div>
        <p v-if="!form.category.trim()" class="mt-1.5 text-[12.5px] text-muted">
          {{ t('planSheet.autoPickFirst') }}
        </p>
        <p v-else-if="autoPreview && !autoPreview.basis" class="mt-1.5 text-[12.5px] text-muted">
          {{ t('planSheet.autoNoHistory') }}
        </p>
        <p
          v-else-if="autoPreview && autoPeriods && autoPreview.basis < autoPeriods"
          class="mt-1.5 text-[12.5px] text-muted"
        >
          {{ t('planSheet.autoShort', { count: autoPreview.basis }) }}
        </p>
      </div>

      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="label" for="pl-cadence">{{ t('planSheet.repeats') }}</label>
          <select id="pl-cadence" v-model="form.cadence" class="input">
            <option v-for="c in CADENCES" :key="c.value" :value="c.value">
              {{ t(cadenceKey(c.value)) }}
            </option>
          </select>
        </div>
        <div>
          <label class="label" for="pl-start">{{ t('planSheet.firstDue') }}</label>
          <input id="pl-start" v-model="form.start_date" type="date" class="input" />
        </div>
      </div>

      <p v-if="preview" class="tnum -mt-1 text-[12.5px] text-muted">
        <template v-if="form.cadence !== 'monthly' && form.cadence !== 'once'">
          {{ t('planSheet.monthlyEquivalent', { amount: fmt(preview.monthly) }) }} ·
        </template>
        <template v-if="preview.next">{{ t('planSheet.nextDue', { day: preview.next }) }}</template>
        <template v-else>{{ t('planSheet.noFuture') }}</template>
      </p>

      <div class="grid grid-cols-2 gap-3">
        <div>
          <label class="label" for="pl-end">
            {{ t('common.endsOn') }} <span class="normal-case">{{ t('common.optional') }}</span>
          </label>
          <input id="pl-end" v-model="form.end_date" type="date" class="input" />
        </div>
      </div>

      <div class="rounded-xl border border-line-soft bg-raised/50 p-3.5">
        <p class="flex items-center gap-2 text-[12.5px] font-semibold text-muted">
          <Icon name="link" :size="15" /> {{ t('planSheet.howItWorks') }}
        </p>
        <p class="mt-1.5 mb-3 text-[12px] text-faint">
          {{ t('planSheet.howItWorksDetail') }}
        </p>
        <div class="mt-3">
          <label class="label" for="pl-tol">{{ t('planSheet.flagIfOff') }}</label>
          <select id="pl-tol" v-model.number="form.tolerance" class="input">
            <option v-for="tol in TOLERANCES" :key="tol.value" :value="tol.value">
              {{ tol.label() }}
            </option>
          </select>
        </div>
      </div>

      <label v-if="existing" class="flex items-center gap-2.5 text-[13.5px]">
        <input v-model="form.archived" type="checkbox" class="size-4 accent-[var(--c-accent)]" />
        {{ t('planSheet.archive') }}
      </label>

      <p v-if="error" class="flex items-center gap-2 text-[13px] text-danger">
        <Icon name="alert" :size="15" /> {{ error }}
      </p>
    </div>

    <template #footer>
      <button v-if="existing" class="btn-danger" @click="destroy">
        <Icon name="trash" :size="16" />
        {{ confirmDelete ? t('common.confirmDelete') : t('common.delete') }}
      </button>
      <div class="flex-1" />
      <button class="btn-soft" @click="closeSheet">{{ t('common.cancel') }}</button>
      <button data-primary :aria-keyshortcuts="ariaCombo('mod+enter')" class="btn-primary" @click="save"><Icon name="check" :size="16" /> {{ t('common.save') }}<Kbd combo="mod+enter" /></button>
    </template>
  </Sheet>
</template>
